import {
    MisoPercentageReduction,
    ScenarioAssumptionsCSV,
    ScenarioAssumptionsCSVFields
} from "in_queue/types/scenarioType";
import { uniq } from "lodash";
import { parse, ParseResult } from "papaparse";
import { parseCsvResults } from "./parseCsvResults";
import { normalizePriorQueueEris } from "./priorQueueAssumptions";
import { dropPartialWholeProjectOnlyAssumptions } from "./wholeProjectOnly";
import { applyWithdrawnDefaults } from "./withdrawnDefaults";

export interface SensitivitiesCsvRules {
    readonly onlyExclusionSupported: boolean;
    readonly validProjectIds: Set<string>;
    readonly speculativeProjectIds: Set<string>;
    // Prior-queue ERIS is withdraw-only, so a partial ERIS reduction becomes a full withdrawal.
    readonly priorQueueProjectIds: ReadonlySet<string>;
    // The ISO studies these in service or out, so a reduction other than 0/0 or 100/100 is dropped.
    readonly wholeProjectOnlyProjectIds: Set<string>;
    // These stay excluded unless the CSV lists them.
    readonly withdrawnProjectIds: Set<string>;
    // The study fixes these, so their rows are skipped without counting as invalid.
    readonly nonEditableProjectIds: ReadonlySet<string>;
}

/** The reduction cells of one CSV row as written, empty when a cell is missing. */
export interface ReceivedReduction {
    readonly eris: string;
    readonly nris: string;
}

export interface SensitivitiesCsvAssumptions {
    readonly assumptions: Record<string, MisoPercentageReduction>;
    readonly includedSpeculativeProjectIds: ReadonlyArray<string>;
    readonly invalidProjectIds: ReadonlyArray<string>;
    readonly nonEditableProjectIds: ReadonlyArray<string>;
    // Every row the id or value check skipped, counting a repeated id once per row.
    readonly skippedRowCount: number;
    readonly priorQueueErisNormalizedIds: ReadonlyArray<string>;
    readonly wholeProjectOnlyDroppedIds: ReadonlyArray<string>;
    readonly withdrawnKeptExcludedIds: ReadonlyArray<string>;
    readonly withdrawnReinstatedIds: ReadonlyArray<string>;
    // Only the skipped projects and those a rule changed, keyed by project id. The last row for an id wins.
    readonly receivedReductions: Readonly<Record<string, ReceivedReduction>>;
}

export type SensitivitiesCsvResult =
    | { readonly ok: false; readonly headerError: string }
    | ({ readonly ok: true } & SensitivitiesCsvAssumptions);

// Papaparse leaves a cell undefined when its row is shorter than the header.
const cellText = (value: unknown): string => String(value ?? "");

const receivedReductionOf = (
    row: ScenarioAssumptionsCSV
): ReceivedReduction => ({
    eris: cellText(row[ScenarioAssumptionsCSVFields.eris_percentage_reduction]),
    nris: cellText(row[ScenarioAssumptionsCSVFields.nris_percentage_reduction])
});

const receivedReductionsOf = (
    rows: ReadonlyArray<ScenarioAssumptionsCSV>,
    projectIds: ReadonlySet<string>
): Record<string, ReceivedReduction> =>
    Object.fromEntries(
        rows
            .filter((row) =>
                projectIds.has(row[ScenarioAssumptionsCSVFields.project_id])
            )
            .map((row) => [
                row[ScenarioAssumptionsCSVFields.project_id],
                receivedReductionOf(row)
            ])
    );

/** Turn a parsed sensitivities CSV into scenario assumptions, listing every project a rule changed. */
export const readSensitivitiesCsv = (
    results: ParseResult<ScenarioAssumptionsCSV>,
    rules: SensitivitiesCsvRules
): SensitivitiesCsvResult => {
    const output = parseCsvResults({
        results,
        onlyExclusionSupported: rules.onlyExclusionSupported,
        validProjectIds: rules.validProjectIds,
        speculativeProjectIds: rules.speculativeProjectIds
    });

    if (output.headerError !== undefined) {
        return { ok: false, headerError: output.headerError };
    }

    const priorQueue = normalizePriorQueueEris(
        output.assumptions,
        rules.priorQueueProjectIds
    );
    const wholeProjectOnly = dropPartialWholeProjectOnlyAssumptions(
        priorQueue.assumptions,
        rules.wholeProjectOnlyProjectIds
    );
    const skippedProjectIds = uniq(output.warnings);
    const invalidProjectIds = skippedProjectIds.filter(
        (id) => !rules.nonEditableProjectIds.has(id)
    );
    const nonEditableProjectIds = skippedProjectIds.filter((id) =>
        rules.nonEditableProjectIds.has(id)
    );

    const withdrawn = applyWithdrawnDefaults({
        assumptions: wholeProjectOnly.assumptions,
        honoredProjectIds: new Set(output.honoredProjectIds),
        withdrawnProjectIds: rules.withdrawnProjectIds
    });

    return {
        ok: true,
        assumptions: withdrawn.assumptions,
        includedSpeculativeProjectIds: output.includedSpeculativeProjectIds,
        invalidProjectIds,
        nonEditableProjectIds,
        skippedRowCount: output.warnings.length,
        priorQueueErisNormalizedIds: priorQueue.normalizedProjectIds,
        wholeProjectOnlyDroppedIds: wholeProjectOnly.droppedProjectIds,
        withdrawnKeptExcludedIds: withdrawn.keptExcludedProjectIds,
        withdrawnReinstatedIds: withdrawn.reinstatedProjectIds,
        receivedReductions: receivedReductionsOf(
            results.data,
            new Set([
                ...skippedProjectIds,
                ...priorQueue.normalizedProjectIds,
                ...wholeProjectOnly.droppedProjectIds
            ])
        )
    };
};

/** Parse a sensitivities CSV file; rejects if the browser cannot read it. */
export const parseSensitivitiesCsvFile = (
    file: File
): Promise<ParseResult<ScenarioAssumptionsCSV>> =>
    new Promise((resolve, reject) =>
        parse<ScenarioAssumptionsCSV>(file, {
            header: true,
            skipEmptyLines: true,
            complete: resolve,
            error: reject
        })
    );
